import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Offcanvas, Form, Button, Row, Col, Spinner } from 'react-bootstrap';
import axios from '../axios';
import { getAbbreviationForPi } from '../utilities';
import './CommunicationLog.css';

const CHANNELS = [
    { value: 'WhatsApp', icon: 'fa-whatsapp', label: 'WhatsApp' },
    { value: 'Phone', icon: 'fa-phone', label: 'Phone' },
    { value: 'Email', icon: 'fa-envelope', label: 'Email' },
    { value: 'Visit', icon: 'fa-handshake-o', label: 'Visit' },
    { value: 'Internal', icon: 'fa-sticky-note-o', label: 'Internal note' },
];
const channelIcon = (c) => (CHANNELS.find((x) => x.value === c) || {}).icon || 'fa-comment';
const TYPE_NAMES = { ORDER: 'Order', PI: 'PI', GROUP_PI: 'Group PI', COMMERCIAL: 'Commercial', BILL: 'Bill' };

// "2026-09-28 11:40:05" -> "28.09.2026 11:40 am"
const formatDateTime = (s) => {
    if (!s) return '';
    const [date, time = ''] = s.split(' ');
    const [y, m, d] = date.split('-');
    const [hh, mm] = time.split(':');
    if (!hh) return `${d}.${m}.${y}`;
    const h = Number(hh);
    return `${d}.${m}.${y} ${h % 12 || 12}:${mm} ${h < 12 ? 'am' : 'pm'}`;
};
const formatDate = (s) => (s ? s.slice(0, 10).split('-').reverse().join('.') : '');
// Today in Bangladesh time as YYYY-MM-DD, to flag follow-ups that are due.
const todayBST = () => new Date(Date.now() + 6 * 3600 * 1000).toISOString().slice(0, 10);
const isDue = (followUpDate) => followUpDate && followUpDate.slice(0, 10) <= todayBST();

const sourceLabel = (entry) => {
    const s = entry.Source || {};
    if (entry.EntityType === 'ORDER') return `Job ${s.JobNumber || entry.EntityId}`;
    if (entry.EntityType === 'PI') return s.JobNumber ? `PI/${getAbbreviationForPi(s.PartyName)}/${s.JobNumber}` : `PI #${entry.EntityId}`;
    if (entry.EntityType === 'COMMERCIAL') return `Com #${entry.EntityId}${s.InvoiceNo ? ` (${s.InvoiceNo})` : ''}`;
    if (entry.EntityType === 'BILL') return `Bill ${s.BillNo || `#${entry.EntityId}`}`;
    if (entry.EntityType === 'GROUP_PI') return `Group PI ${s.GroupPiNo || `#${entry.EntityId}`}`;
    return entry.EntityType;
};

// Entry counts + current status for the rows on a list page. Fails quietly: the list still works without it.
export function useCommLogSummary(type, ids) {
    const [summary, setSummary] = useState({});
    const idsKey = ids.join(',');

    const refresh = useCallback(() => {
        if (!idsKey) { setSummary({}); return; }
        axios
            .post(`${window.backendUrl}/communication_log_summary`, { type, ids: idsKey.split(',') })
            .then((res) => setSummary(res.data || {}))
            .catch((err) => console.error('Error loading communication log summary:', err));
    }, [type, idsKey]);

    useEffect(() => { refresh(); }, [refresh]);
    return [summary, refresh];
}

// The 💬 button with the entry count.
export function CommLogButton({ info, onClick }) {
    const count = info?.count || 0;
    return (
        <span className="comm-log-btn" title="Communication log" onClick={onClick}>
            <i className="fa fa-comments"></i>{' '}
            <span className={`badge ${count ? 'bg-primary' : 'bg-secondary'}`}>{count}</span>
        </span>
    );
}

// One-line current status shown in the list row.
export function CommLogStatus({ info, onClick }) {
    const status = info?.status;
    if (!status) return null;
    return (
        <div className="comm-log-status" onClick={onClick} title="Open communication log">
            {status.StatusText}
            <small>
                {formatDate(status.CreatedAt)}
                {status.CreatedByName ? ` · ${status.CreatedByName}` : ''}
                {status.FollowUpDate && (
                    <span className={isDue(status.FollowUpDate) ? 'comm-log-due' : ''}> · follow up {formatDate(status.FollowUpDate)}</span>
                )}
            </small>
        </div>
    );
}

const emptyForm = { channel: 'WhatsApp', direction: 'OUT', contactPerson: '', message: '', followUpDate: '', isStatus: false, statusText: '' };

// Side panel: current status, "add entry" form and history.
// target = { type: 'ORDER' | 'PI' | 'GROUP_PI' | 'COMMERCIAL' | 'BILL', id, title, subtitle } or null when closed.
export function CommunicationLogDrawer({ target: openTarget, onHide, onSaved }) {
    // Keep the last document while the panel slides closed; `openTarget` is already null by then.
    const lastTarget = useRef(null);
    if (openTarget) lastTarget.current = openTarget;
    const target = lastTarget.current;

    const [data, setData] = useState({ entries: [], currentStatus: null });
    const [loading, setLoading] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('ALL');

    const load = useCallback(() => {
        if (!openTarget) return;
        setLoading(true);
        axios
            .get(`${window.backendUrl}/communication_log/${target.type}/${target.id}`)
            .then((res) => setData(res.data))
            .catch((err) => {
                console.error('Error loading communication log:', err);
                setError('Could not load the communication log.');
            })
            .finally(() => setLoading(false));
    }, [openTarget]);

    useEffect(() => {
        if (!openTarget) return;
        setData({ entries: [], currentStatus: null });
        setForm(emptyForm);
        setError('');
        setFilter('ALL');
        load();
    }, [load]);

    const set = (field, value) => setForm((f) => ({ ...f, [field]: value }));

    const toggleStatus = (checked) => {
        // Pre-fill the status line with the first line of the message; staff can shorten it.
        const firstLine = form.message.trim().split(/\n/)[0].slice(0, 255);
        setForm((f) => ({ ...f, isStatus: checked, statusText: checked && !f.statusText ? firstLine : f.statusText }));
    };

    const save = () => {
        if (!openTarget) return;
        if (!form.message.trim()) { setError('Please write what was said.'); return; }
        if (form.isStatus && !form.statusText.trim()) { setError('Please write the current status.'); return; }
        setSaving(true);
        setError('');
        axios
            .post(`${window.backendUrl}/communication_log`, {
                entityType: target.type,
                entityId: target.id,
                channel: form.channel,
                direction: form.direction,
                contactPerson: form.contactPerson,
                message: form.message,
                statusText: form.isStatus ? form.statusText : '',
                followUpDate: form.followUpDate || null,
            })
            .then(() => {
                setForm(emptyForm);
                load();
                if (onSaved) onSaved();
            })
            .catch((err) => setError(err.response?.data?.error || 'Failed to save entry.'))
            .finally(() => setSaving(false));
    };

    const entries = data.entries || [];
    const types = [...new Set(entries.map((e) => e.EntityType))];
    const shown = entries.filter((e) => filter === 'ALL' || e.EntityType === filter);
    const current = data.currentStatus;

    return (
        <Offcanvas show={!!openTarget} onHide={onHide} placement="end" className="comm-log-drawer">
            <Offcanvas.Header closeButton>
                <div>
                    <div className="comm-log-kicker">COMMUNICATION LOG</div>
                    <Offcanvas.Title as="h5">{target?.title}</Offcanvas.Title>
                    {target?.subtitle && <div className="comm-log-subtitle">{target.subtitle}</div>}
                </div>
            </Offcanvas.Header>
            <Offcanvas.Body>
                <div className="comm-log-current">
                    <div className="comm-log-current-label"><i className="fa fa-thumb-tack"></i> Current status</div>
                    <div className="comm-log-current-text">{current ? current.StatusText : 'No status set yet'}</div>
                    {current && (
                        <div className="comm-log-meta">
                            Set by {current.CreatedByName || 'unknown'} on {formatDateTime(current.CreatedAt)}
                            {current.FollowUpDate && ` · follow up ${formatDate(current.FollowUpDate)}`}
                        </div>
                    )}
                </div>

                <div className="comm-log-add">
                    <div className="comm-log-section-title">Add entry</div>
                    <div>
                        {CHANNELS.map((c) => (
                            <button
                                key={c.value}
                                type="button"
                                className={`comm-log-chan-btn ${form.channel === c.value ? 'active' : ''}`}
                                onClick={() => set('channel', c.value)}
                            >
                                <i className={`fa ${c.icon}`}></i> {c.label}
                            </button>
                        ))}
                    </div>
                    {form.channel !== 'Internal' && (
                        <Row className="g-2 mt-1">
                            <Col xs={6}>
                                <Form.Control size="sm" placeholder="Talked to (e.g. Mr. Karim)" value={form.contactPerson} onChange={(e) => set('contactPerson', e.target.value)} />
                            </Col>
                            <Col xs={6}>
                                <Form.Select size="sm" value={form.direction} onChange={(e) => set('direction', e.target.value)}>
                                    <option value="OUT">We contacted them</option>
                                    <option value="IN">They contacted us</option>
                                </Form.Select>
                            </Col>
                        </Row>
                    )}
                    <Form.Control
                        as="textarea"
                        size="sm"
                        rows={3}
                        className="mt-2"
                        placeholder="What was said / agreed? You can paste the WhatsApp message here."
                        value={form.message}
                        onChange={(e) => set('message', e.target.value)}
                    />
                    <Row className="g-2 mt-1 align-items-end">
                        <Col xs={6}>
                            <Form.Label className="comm-log-small-label">Follow up on (optional)</Form.Label>
                            <Form.Control size="sm" type="date" value={form.followUpDate} onChange={(e) => set('followUpDate', e.target.value)} />
                        </Col>
                        <Col xs={6}>
                            <Form.Check
                                id="comm-log-is-status"
                                label="Make this the current status"
                                className="comm-log-small-check"
                                checked={form.isStatus}
                                onChange={(e) => toggleStatus(e.target.checked)}
                            />
                        </Col>
                    </Row>
                    {form.isStatus && (
                        <Form.Control
                            size="sm"
                            className="mt-2"
                            maxLength={255}
                            placeholder="Short status, e.g. Waiting for party to confirm colour"
                            value={form.statusText}
                            onChange={(e) => set('statusText', e.target.value)}
                        />
                    )}
                    {error && <div className="text-danger mt-2" style={{ fontSize: '12px' }}>{error}</div>}
                    <div className="text-end mt-2">
                        <Button size="sm" style={{ backgroundColor: 'rgb(43, 111, 189)', border: 0 }} disabled={saving} onClick={save}>
                            {saving ? 'Saving...' : 'Save entry'}
                        </Button>
                    </div>
                </div>

                <div className="d-flex justify-content-between align-items-center mb-2">
                    <div className="comm-log-section-title mb-0">History</div>
                    {types.length > 1 && (
                        <div>
                            {['ALL', ...types].map((t) => (
                                <span key={t} className={`comm-log-chip ${filter === t ? 'active' : ''}`} onClick={() => setFilter(t)}>
                                    {t === 'ALL' ? 'All' : TYPE_NAMES[t]}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {loading ? (
                    <div className="text-center"><Spinner size="sm" animation="border" /></div>
                ) : shown.length === 0 ? (
                    <div className="text-muted" style={{ fontSize: '13px' }}>No entries yet. Add the first one above.</div>
                ) : (
                    <ul className="comm-log-timeline">
                        {shown.map((e) => {
                            const fromOther = !(e.EntityType === target.type && e.EntityId === target.id);
                            return (
                                <li key={e.LogId} className={`comm-log-item ${e.StatusText ? 'is-status' : ''}`}>
                                    <div className="comm-log-meta">
                                        <span className={`comm-log-chan ${e.Channel}`}><i className={`fa ${channelIcon(e.Channel)}`}></i> {e.Channel}</span>
                                        {e.Direction && ` · ${e.Direction === 'IN' ? 'They contacted us' : 'We contacted them'}`}
                                        {e.ContactPerson && <> · <b>{e.ContactPerson}</b></>}
                                        {fromOther && <span className={`comm-log-src ${e.EntityType.toLowerCase()}`}>from {sourceLabel(e)}</span>}
                                    </div>
                                    {e.StatusText && (
                                        <div className="comm-log-status-line"><i className="fa fa-thumb-tack"></i> Status: {e.StatusText}</div>
                                    )}
                                    <div className="comm-log-text">{e.Message}</div>
                                    <div className="comm-log-meta">
                                        {formatDateTime(e.CreatedAt)} · {e.CreatedByName || 'unknown'}
                                        {e.FollowUpDate && (
                                            <span className={isDue(e.FollowUpDate) ? 'comm-log-due' : ''}> · <i className="fa fa-bell-o"></i> follow up {formatDate(e.FollowUpDate)}</span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
}
